import { GetContactInformationMyMSCResponse } from '@msc-b2c/interfaces/interfaces';
import { Level } from '@msc-b2c/interfaces/types';
import dayjs, { Dayjs } from 'dayjs';

/**
 * Checks whether the user has filled in at least one contact field.
 *
 * The function verifies if the provided customer information object exists
 * and contains at least one of the following properties:
 * - mobilePhone
 * - phone
 * - address
 *
 * @param {GetContactInformationMyMSCResponse | undefined} customerInfo - The customer contact information object.
 * @returns {boolean} Returns `true` if at least one contact field is present, otherwise `false`.
 */
export const hasUserFilledData = (
  customerInfo: GetContactInformationMyMSCResponse | undefined
): boolean =>
  !!customerInfo && (!!customerInfo.mobilePhone || !!customerInfo.phone || !!customerInfo.address);

/**
 * Extracts the cruise departure date from a cruise ID.
 *
 * The function searches for the first 8 consecutive digits (YYYYMMDD format)
 * inside the cruiseId string and converts it into a Dayjs instance.
 *
 * @param {string} cruiseId - The cruise identifier containing a date in YYYYMMDD format.
 * @returns {Dayjs | null} A Dayjs instance representing the extracted date,
 * or `null` if no valid date pattern is found.
 */
export const getCruiseDateFromCruiseId = (cruiseId: string): Dayjs | null => {
  const match = /\d{8}/.exec(cruiseId);
  if (!match) {
    console.error(
      '[myAreaVoyagerClubUtils.getCruiseDateFromCruiseId] cannot find date inside cruise id',
      cruiseId
    );

    return null;
  }

  return dayjs(match[0], 'YYYYMMDD');
};

/**
 * Calculates the number of remaining days from today to a given end date.
 *
 * The difference is calculated in whole days using Dayjs `diff`.
 * A negative value indicates that the date is in the past.
 *
 * @param {Dayjs} endDate - The target end date.
 * @returns {number} The number of days between today and the provided end date.
 */
export const getRemainingDays = (endDate: Dayjs): number => {
  return endDate.diff(dayjs(), 'day');
};

/**
 * Calculates the overall progression percentage across all levels.
 *
 * The function determines:
 * - The current level based on the user's points
 * - The fractional progress within the current level segment
 * - The total progression percentage across the full level ladder
 *
 * The returned value represents the user's total progress from
 * the first level to the last level as a percentage (0–100).
 *
 * @param {Level[]} levels - Ordered list of levels (ascending by `pointsRequired`).
 * @param {number} points - The user's current total points.
 *
 * @returns {number} A percentage value between 0 and 100 representing
 * the user's total progress across all levels.
 */
export const getUserLevelTotalProgress = (levels: Level[], points: number): number => {
  const levelIndex = Math.max(
    levels.findLastIndex((lvl: Level) => points >= (lvl.pointsRequired ?? 0)),
    0
  );

  const currentLevel = levels[levelIndex];
  const nextLevel = levels[levelIndex + 1];

  let segmentProgress = 0;

  if (nextLevel) {
    const min = currentLevel.pointsRequired ?? 0;
    const max = nextLevel.pointsRequired ?? 0;
    segmentProgress = (points - min) / (max - min);
  } else {
    segmentProgress = 1;
  }

  return ((levelIndex + segmentProgress) / (levels.length - 1)) * 100;
};
